import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import { Button, Divider, Grid, InputAdornment, Stack, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { FormikDecimalTextField } from "../../../../../../formik/FormikDecimalTextField";
import { nameof } from "../../../../../../utils/nameof";
import { ClaimStatusKey } from "../../../../claimStatus";
import { ClaimFormValues, ExpectedLossPercentRange } from "../../ClaimFormFields.types";
import { SectionHeader } from "../section-header/SectionHeader";
import { COLLATERAL_TYPE_ORDER, createEmptyCollateral } from "./Collaterals.utils";
import { CollateralBlock } from "./components/collateral-block/CollateralBlock";
import { useCollateralHoldingOptions } from "./hooks/useCollateralHoldingOptions";

const HALF = 6;
const FULL = 12;

interface Props {
  formik: FormikProps<ClaimFormValues>;
}

export function CollateralSection({ formik }: Props) {
  const { t } = useTranslation();
  const { collateralStatus, collaterals } = formik.values;
  const { optionsFor } = useCollateralHoldingOptions(collaterals);

  const typeOptions = useMemo(
    () => COLLATERAL_TYPE_ORDER.map((type) => ({ id: type, label: t(`claims.collaterals.types.${type}`) })),
    [t]
  );

  const isSecured = collateralStatus === ClaimStatusKey.Yes;

  const addCollateral = () => {
    void formik.setFieldValue(nameof<ClaimFormValues>("collaterals"), [...collaterals, createEmptyCollateral()]);
  };

  const removeCollateral = (key: string) => {
    void formik.setFieldValue(
      nameof<ClaimFormValues>("collaterals"),
      collaterals.filter((collateral) => collateral.key !== key)
    );
  };

  if (!isSecured) {
    return null;
  }

  return (
    <>
      <Grid size={FULL}>
        <Divider />
      </Grid>

      <Grid size={FULL}>
        <SectionHeader>{t("claims.form.collateralSection")}</SectionHeader>
      </Grid>

      <Grid size={HALF}>
        <FormikDecimalTextField
          formik={formik}
          fieldName="expectedLossPercent"
          label={t("claims.collaterals.expectedLoss")}
          required
          decimalScale={2}
          minValue={ExpectedLossPercentRange.min}
          maxValue={ExpectedLossPercentRange.max}
          slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
        />
      </Grid>

      <Grid size={FULL}>
        <Stack gap={2}>
          {collaterals.map((collateral, index) => (
            <CollateralBlock
              key={collateral.key}
              formik={formik}
              index={index}
              typeOptions={typeOptions}
              holdingOptionsFor={optionsFor}
              onDelete={() => removeCollateral(collateral.key)}
            />
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addCollateral}
            sx={{ borderStyle: "dashed", "&:hover": { borderStyle: "dashed" } }}
            fullWidth
          >
            {t("claims.collaterals.add")}
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            aria-live="polite"
            sx={{ display: "block", textAlign: "right" }}
          >
            {t("claims.collaterals.count", { count: collaterals.length })}
          </Typography>
        </Stack>
      </Grid>
    </>
  );
}
